/* sync.js — backfill + incremental sync engine.
   The first run pages newest-first until it passes the configured start date;
   every later run pages from the top until it reaches the stored watermark,
   so a sync with nothing new costs exactly one API call. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const MAX_PAGES = 50; // safety cap: 50 * 1000 = up to 50k files per run

  function nowISO() { return new Date().toISOString(); }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || null }));
  }

  function resolveEffectiveStartDate() {
    const s = CAD.storage.getSettings();
    if (s.startDateMode === 'all') return null;
    if (s.startDateMode === 'custom') {
      if (!s.startDate) return null;
      const d = new Date(s.startDate + 'T00:00:00'); // local midnight of the chosen day
      return isNaN(d.getTime()) ? null : d;
    }
    const days = parseInt(s.presetDays, 10) || 30;
    return new Date(Date.now() - days * 86400000);
  }

  function normalizeAndClassify(raw, taxonomy) {
    if (!raw || typeof raw !== 'object') { console.warn('[sync] Skipping non-object record.', raw); return null; }
    if (!raw.id || !raw.created_at) { console.warn('[sync] Skipping record missing id/created_at.', raw); return null; }

    const record = {
      id: raw.id,
      filename: raw.filename || '(unnamed)',
      mimeType: raw.mime_type || null,
      sizeBytes: typeof raw.size_bytes === 'number' ? raw.size_bytes : null,
      createdAt: raw.created_at,
      expiresAt: raw.expires_at || null,
      downloadable: !!raw.downloadable,
    };
    // Inferred heuristic: user uploads are never downloadable; code-execution /
    // skill outputs are. This is the only direction signal a plain key exposes.
    record.direction = record.downloadable ? 'generated' : 'uploaded';

    const cls = CAD.classify.classifyFilename(record.filename, taxonomy);
    record.categoryId = cls.categoryId;
    record.sensitivity = cls.sensitivity;
    record.matchedKeyword = cls.matchedKeyword;
    return record;
  }

  // A sync run keeps the whole file map in memory and flushes it to storage
  // once per page; the id-presence guard below then costs nothing per record.
  function makeCache() {
    const files = CAD.storage.getFiles();
    return {
      files: files,
      has: function (id) { return Object.prototype.hasOwnProperty.call(files, id); },
      put: function (rec) { files[rec.id] = rec; },
      flush: function () { CAD.storage.saveFiles(files); },
      count: function () { return Object.keys(files).length; },
    };
  }

  async function runInitialBackfill(cache) {
    const startDate = resolveEffectiveStartDate();
    let page, pagesFetched = 0, newestSeen = null;

    while (pagesFetched < MAX_PAGES) {
      const res = await CAD.api.fetchFilesPage({ limit: 1000, page: page });
      pagesFetched++;
      for (let i = 0; i < res.data.length; i++) {
        const raw = res.data[i];
        if (newestSeen === null) newestSeen = raw.created_at; // first item overall = newest
        if (startDate && new Date(raw.created_at) < startDate) {
          cache.flush();
          await finishBackfill(newestSeen); // everything after this is even older
          return;
        }
        const rec = normalizeAndClassify(raw, CAD.storage.getClassification());
        if (rec) cache.put(rec);
      }
      cache.flush();
      if (!res.nextPage) break;
      page = res.nextPage;
    }
    await finishBackfill(newestSeen);
  }

  async function finishBackfill(newestSeen) {
    CAD.storage.patchSyncMeta({ initialBackfillComplete: true, watermarkNewestCreatedAt: newestSeen });
  }

  async function runIncrementalSync(cache) {
    const meta = CAD.storage.getSyncMeta();
    const watermark = meta.watermarkNewestCreatedAt ? new Date(meta.watermarkNewestCreatedAt) : new Date(0);
    let page, newestThisRun = null, pagesFetched = 0;

    while (pagesFetched < MAX_PAGES) {
      const res = await CAD.api.fetchFilesPage({ limit: 1000, page: page });
      pagesFetched++;
      let hitWatermark = false;
      for (let i = 0; i < res.data.length; i++) {
        const raw = res.data[i];
        if (newestThisRun === null) newestThisRun = raw.created_at;
        // The id check is the real dedupe guard: two files can share a
        // created_at second, so timestamps alone are not a safe stop condition.
        if (new Date(raw.created_at) <= watermark && cache.has(raw.id)) {
          hitWatermark = true;
          break;
        }
        const rec = normalizeAndClassify(raw, CAD.storage.getClassification());
        if (rec) cache.put(rec);
      }
      cache.flush();
      if (hitWatermark || !res.nextPage) break;
      page = res.nextPage;
    }
    if (newestThisRun) CAD.storage.patchSyncMeta({ watermarkNewestCreatedAt: newestThisRun });
  }

  /**
   * Public entry point. Returns true on success, false on failure or no-op.
   * opts.manual marks a user-initiated sync (drives toasts instead of the banner only).
   */
  async function runSync(opts) {
    opts = opts || {};
    const meta = CAD.storage.getSyncMeta();
    if (meta.lastSyncStatus === 'running') {
      if (opts.manual && CAD.util) CAD.util.toast('A sync is already running.');
      return false;
    }

    CAD.storage.patchSyncMeta({ lastSyncStatus: 'running', lastSyncStartedAt: nowISO(), lastError: null });
    emit('cad:sync-start', { manual: !!opts.manual });

    try {
      const cache = makeCache();
      if (!meta.initialBackfillComplete) await runInitialBackfill(cache);
      else await runIncrementalSync(cache);
      cache.flush();
      CAD.storage.patchSyncMeta({
        lastSyncStatus: 'success',
        lastSyncCompletedAt: nowISO(),
        lastError: null,
        totalFilesStored: cache.count(),
      });
      emit('cad:sync-complete', { manual: !!opts.manual });
      return true;
    } catch (err) {
      console.error('[sync] Sync failed:', err);
      CAD.storage.patchSyncMeta({
        lastSyncStatus: 'error',
        lastSyncCompletedAt: nowISO(),
        lastError: { type: (err && err.type) || 'UNKNOWN', message: CAD.api.describeError(err) },
      });
      emit('cad:sync-error', { manual: !!opts.manual, error: err });
      return false;
    }
  }

  CAD.sync = { runSync: runSync, resolveEffectiveStartDate: resolveEffectiveStartDate };
})();
